import React, { useState, useMemo } from "react";
import MainLayout from "../components/layout/MainLayout";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { API_BASE_URL } from "../config";
import {
  Globe,
  CheckCircle2,
  RotateCw,
  Search,
  ArrowRight,
  ShieldCheck,
  Ban,
  Info,
  X,
  History,
  ChevronDown,
  ChevronUp
} from "lucide-react";

interface BackupDomain {
  domain: string;
  geo: "SE" | "NO" | "FI" | "DE";
  role: "primary" | "secondary" | "alternate";
  niche: "Offers" | "Finans / Lån" | "Casino / iGaming";
  createdAt: string;
  dnsStatus: "Klar (Cloudflare)" | "Parkeret (GoDaddy)" | "Klar (Porkbun)";
  notes?: string;
}

interface RetiredDomain {
  domain: string;
  usedPeriod: string;
  notes?: string;
}

interface MarketConfig {
  geo: "SE" | "NO" | "FI" | "DE";
  countryName: string;
  flag: string;
  feedblitzAccount: string;
  activeDomain: {
    domain: string;
    senderEmail: string;
    createdAt: string;
    activeSince: string;
  };
  backups: BackupDomain[];
  retired: RetiredDomain[];
}

const INITIAL_MARKETS: MarketConfig[] = [
  {
    geo: "SE",
    countryName: "Sverige",
    flag: "🇸🇪",
    feedblitzAccount: "fb_se",
    activeDomain: {
      domain: "klarkredit24.se",
      senderEmail: "post@klarkredit24.se",
      createdAt: "2026-06-15",
      activeSince: "2026-09-14"
    },
    backups: [
      { domain: "extraerbjudanden.com", geo: "SE", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup" },
      { domain: "svenskaspartips.com", geo: "SE", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup" },
      { domain: "nordiskaerbjudanden.com", geo: "SE", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup" }
    ],
    retired: [
      { domain: "svenskarabatter.com", usedPeriod: "30. okt 2026 – 31. okt 2026", notes: "Blokeret på Spamhaus DBL 31. okt 2026 – rullet tilbage til klarkredit24.se" },
      { domain: "sparerbjudanden.com", usedPeriod: "14. sep 2026", notes: "Blokeret på Spamhaus DBL 14. sep 2026 – erstattet af klarkredit24.se" },
      { domain: "kreditmark.se", usedPeriod: "1. sep 2026 – 14. sep 2026", notes: "Brugt i 13 dage – erstattet af klarkredit24.se" },
      { domain: "nyhetsgiganten.se", usedPeriod: "1. sep 2026", notes: "Kortvarigt aktiv – erstattet af kreditmark.se" },
      { domain: "bastaerbjudanden.com", usedPeriod: "19. aug 2026 – 1. sep 2026", notes: "Brugt i 13 dage – erstattet af nyhetsgiganten.se" },
      { domain: "svenskaerbjudanden.se", usedPeriod: "11. aug 2026 – 19. aug 2026", notes: "Brugt i 8 dage – erstattet af bastaerbjudanden.com" },
      { domain: "onlinelaan.com", usedPeriod: "Indtil 11. aug 2026", notes: "Tidligere aktivt afsenderdomæne" }
    ]
  },
  {
    geo: "NO",
    countryName: "Norge",
    flag: "🇳🇴",
    feedblitzAccount: "fb_no",
    activeDomain: {
      domain: "provepakker.com",
      senderEmail: "post@provepakker.com",
      createdAt: "2024-10-15",
      activeSince: "2026-09-18"
    },
    backups: [
      { domain: "bestefordeler.com", geo: "NO", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup" },
      { domain: "norskespartips.com", geo: "NO", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup" },
      { domain: "superrabatter.com", geo: "NO", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup" },
      { domain: "dinetilbud.com", geo: "NO", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup" }
    ],
    retired: [
      { domain: "billigelan.com", usedPeriod: "14. sep 2026 – 18. sep 2026", notes: "Blokeret på Spamhaus DBL 18. sep 2026 – erstattet af provepakker.com" },
      { domain: "norskekampanjer.com", usedPeriod: "14. sep 2026", notes: "Blokeret på Spamhaus DBL 14. sep 2026 – erstattet af billigelan.com" },
      { domain: "norsktilbud.com", usedPeriod: "11. aug 2026 – 28. aug 2026", notes: "Erstattet af provepakker.com d. 28. aug 2026" },
      { domain: "onlinetilbud.com", usedPeriod: "Indtil 11. aug 2026", notes: "Erstattet af norsktilbud.com d. 11. aug 2026" }
    ]
  },
  {
    geo: "FI",
    countryName: "Finland",
    flag: "🇫🇮",
    feedblitzAccount: "fb_fi",
    activeDomain: {
      domain: "parhaatedut.com",
      senderEmail: "post@parhaatedut.com",
      createdAt: "2026-08-28",
      activeSince: "2026-09-14"
    },
    backups: [
      { domain: "suomenedut.com", geo: "FI", role: "primary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup (Finske fordele)" },
      { domain: "edullisettarjoukset.com", geo: "FI", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup (Gode tilbud)" },
      { domain: "suomensuositut.com", geo: "FI", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup (Populære tilbud)" },
      { domain: "tarjouspostia.com", geo: "FI", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Cloudflare)", notes: "Nyregistreret Porkbun backup (Tilbudspost)" }
    ],
    retired: [
      { domain: "finnlanetditt.com", usedPeriod: "1. sep 2026 – 14. sep 2026", notes: "Brugt i 13 dage – erstattet af parhaatedut.com" },
      { domain: "parhaatlainatarjoukset.com", usedPeriod: "19. aug 2026 – 1. sep 2026", notes: "Brugt i 13 dage – erstattet af finnlanetditt.com" },
      { domain: "tarjouksetsuomessa.com", usedPeriod: "11. aug 2026 – 19. aug 2026", notes: "Brugt i 8 dage – erstattet af parhaatlainatarjoukset.com" },
      { domain: "tarjouksetpostitse.com", usedPeriod: "Indtil 11. aug 2026", notes: "Tidligere aktivt afsenderdomæne" }
    ]
  },
  {
    geo: "DE",
    countryName: "Tyskland",
    flag: "🇩🇪",
    feedblitzAccount: "fb_de",
    activeDomain: {
      domain: "sparfuechse-aktuell.de",
      senderEmail: "post@sparfuechse-aktuell.de",
      createdAt: "2026-08-28",
      activeSince: "2026-09-14"
    },
    backups: [
      { domain: "spar-empfehlungen.de", geo: "DE", role: "primary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Porkbun)", notes: "Nyregistreret tysk Porkbun backup" },
      { domain: "deinespartipps.de", geo: "DE", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Porkbun)", notes: "Nyregistreret tysk Porkbun backup" },
      { domain: "rabatt-post.de", geo: "DE", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Porkbun)", notes: "Nyregistreret tysk Porkbun backup" },
      { domain: "aktuelle-rabatte.de", geo: "DE", role: "secondary", niche: "Offers", createdAt: "2026-08-28", dnsStatus: "Klar (Porkbun)", notes: "Nyregistreret tysk Porkbun backup" },
      { domain: "casinokanzler.com", geo: "DE", role: "alternate", niche: "Casino / iGaming", createdAt: "2025-05-15", dnsStatus: "Parkeret (GoDaddy)", notes: "Casino portal" }
    ],
    retired: [
      { domain: "bonusratgeber.de", usedPeriod: "1. sep 2026 – 14. sep 2026", notes: "Brugt i 13 dage – erstattet af sparfuechse-aktuell.de" },
      { domain: "onlineerbjudanden.com", usedPeriod: "19. aug 2026 – 1. sep 2026", notes: "Brugt i 13 dage – erstattet af bonusratgeber.de" },
      { domain: "gewinnspielen.com", usedPeriod: "11. aug 2026 – 19. aug 2026", notes: "Brugt i 8 dage – erstattet af onlineerbjudanden.com" },
      { domain: "kreditoptionen.de", usedPeriod: "Indtil 11. aug 2026", notes: "Tidligere aktivt afsenderdomæne" }
    ]
  }
];

function calculateDomainAge(dateStr: string) {
  const diffTime = Math.abs(new Date().getTime() - new Date(dateStr).getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays <= 30) {
    return `${diffDays} ${diffDays === 1 ? "dag" : "dage"}`;
  }
  const years = (diffDays / 365.25).toFixed(1);
  return `${years} år`;
}

function calculateDaysActive(dateStr: string) {
  const diffTime = Math.abs(new Date().getTime() - new Date(dateStr).getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

function formatDateDanish(dateStr: string) {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

export default function FeedblitzDomainRotationPage() {
  const [markets, setMarkets] = useState<MarketConfig[]>(INITIAL_MARKETS);
  const [selectedGeo, setSelectedGeo] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isActivating, setIsActivating] = useState<boolean>(false);
  const [activationMessage, setActivationMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [expandedRetired, setExpandedRetired] = useState<Record<string, boolean>>({
    SE: false,
    NO: false,
    FI: false,
    DE: false
  });
  const [rotationTarget, setRotationTarget] = useState<{
    market: MarketConfig;
    newDomain: BackupDomain;
  } | null>(null);

  const toggleRetired = (geo: string) => {
    setExpandedRetired((prev) => ({ ...prev, [geo]: !prev[geo] }));
  };

  const handleRotateConfirm = async () => {
    if (!rotationTarget) return;

    const { market, newDomain } = rotationTarget;
    const todayStr = new Date().toISOString().slice(0, 10);
    setIsActivating(true);
    setActivationMessage(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/domain-rotation/activate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          domain: newDomain.domain,
          geo: market.geo,
          esp_id: `feedblitz_${market.geo.toLowerCase()}`,
          retired_domain: market.activeDomain.domain
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActivationMessage({
          type: "success",
          text: `✅ ${newDomain.domain} er aktiveret! DNS-records (SPF, DKIM, DMARC) er automatisk oprettet hos Cloudflare.`
        });
      } else {
        setActivationMessage({
          type: "error",
          text: `⚠️ Fejl under automatisk DNS-opsætning: ${data.error || 'Ukendt fejl'}`
        });
      }
    } catch (e: any) {
      console.error("[DomainRotation] Error:", e);
      setActivationMessage({
        type: "error",
        text: `⚠️ Netværksfejl under aktivering: ${e.message}`
      });
    } finally {
      setIsActivating(false);
    }

    setMarkets((prev) =>
      prev.map((m) => {
        if (m.geo !== market.geo) return m;

        // Old active moves directly to RETIRED (Do not reuse)
        const oldActiveAsRetired: RetiredDomain = {
          domain: m.activeDomain.domain,
          usedPeriod: `${formatDateDanish(m.activeDomain.activeSince)} – ${formatDateDanish(todayStr)}`,
          notes: `Aktiv i ${calculateDaysActive(m.activeDomain.activeSince)} dage – erstattet af ${newDomain.domain}`
        };

        // Remove newDomain from backups
        const newBackups = m.backups.filter((b) => b.domain !== newDomain.domain);
        const newRetired = [oldActiveAsRetired, ...m.retired];

        return {
          ...m,
          activeDomain: {
            domain: newDomain.domain,
            senderEmail: `post@${newDomain.domain}`,
            createdAt: newDomain.createdAt,
            activeSince: todayStr
          },
          backups: newBackups,
          retired: newRetired
        };
      })
    );

    setRotationTarget(null);
  };

  const filteredMarkets = useMemo(() => {
    return markets
      .filter((m) => selectedGeo === "ALL" || m.geo === selectedGeo)
      .map((m) => {
        if (!searchQuery.trim()) return m;

        const q = searchQuery.toLowerCase();
        const activeMatches =
          m.activeDomain.domain.toLowerCase().includes(q) ||
          m.countryName.toLowerCase().includes(q);

        const filteredBackups = m.backups.filter(
          (b) =>
            b.domain.toLowerCase().includes(q) ||
            b.niche.toLowerCase().includes(q) ||
            (b.notes && b.notes.toLowerCase().includes(q))
        );

        const filteredRetired = m.retired.filter(
          (r) =>
            r.domain.toLowerCase().includes(q) ||
            r.usedPeriod.toLowerCase().includes(q) ||
            (r.notes && r.notes.toLowerCase().includes(q))
        );

        if (activeMatches) return m;
        return {
          ...m,
          backups: filteredBackups,
          retired: filteredRetired
        };
      })
      .filter((m) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
          m.activeDomain.domain.toLowerCase().includes(q) ||
          m.countryName.toLowerCase().includes(q) ||
          m.backups.length > 0 ||
          m.retired.length > 0
        );
      });
  }, [markets, selectedGeo, searchQuery]);

  const totalActive = markets.length;
  const totalBackups = markets.reduce((acc, m) => acc + m.backups.length, 0);
  const totalRetired = markets.reduce((acc, m) => acc + m.retired.length, 0);

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto space-y-6 pb-12">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-200 pb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2.5">
              <Globe className="w-6 h-6 text-indigo-600" />
              FeedBlitz Domænerotation
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Oversigt over aktive afsenderdomæner, reservepuljer og udfasede domæner for SE, NO, FI og DE.
            </p>
          </div>

          <div className="flex items-center gap-3 text-xs text-gray-600 bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl">
            <div>
              <span className="font-semibold text-gray-900">{totalActive}</span> aktive afsendere
            </div>
            <span className="text-gray-300">•</span>
            <div>
              <span className="font-semibold text-gray-900">{totalBackups}</span> reserver i puljen
            </div>
            <span className="text-gray-300">•</span>
            <div>
              <span className="font-semibold text-gray-500">{totalRetired}</span> udfasede
            </div>
          </div>
        </div>

        {/* Activation Feedback Banner */}
        {activationMessage && (
          <div
            className={`p-4 rounded-xl text-sm font-medium border flex items-center justify-between ${
              activationMessage.type === "success"
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-amber-50 text-amber-800 border-amber-200"
            }`}
          >
            <div>{activationMessage.text}</div>
            <button
              onClick={() => setActivationMessage(null)}
              className="text-xs text-gray-500 hover:text-gray-900 underline ml-4 cursor-pointer"
            >
              Luk
            </button>
          </div>
        )}

        {/* Filter & Search Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-2 rounded-xl border border-gray-200">
          <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
            {[
              { id: "ALL", label: "Alle markeder" },
              { id: "SE", label: "🇸🇪 Sverige" },
              { id: "NO", label: "🇳🇴 Norge" },
              { id: "FI", label: "🇫🇮 Finland" },
              { id: "DE", label: "🇩🇪 Tyskland" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedGeo(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  selectedGeo === tab.id
                    ? "bg-indigo-600 text-white shadow-xs"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Søg domæne, niche..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-60 pl-9 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
            />
          </div>
        </div>

        {/* Market Sections */}
        <div className="space-y-6">
          {filteredMarkets.map((market) => {
            const isRetiredOpen = expandedRetired[market.geo] || Boolean(searchQuery.trim());

            return (
              <Card key={market.geo} className="border-gray-200 shadow-xs overflow-hidden">
                {/* Market Header */}
                <CardHeader className="bg-gray-50/80 px-5 py-3.5 border-b border-gray-200 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xl">{market.flag}</span>
                    <div>
                      <h2 className="text-base font-bold text-gray-900 leading-tight">
                        {market.countryName}
                      </h2>
                      <span className="text-xs text-gray-500 font-mono">
                        FeedBlitz: {market.feedblitzAccount}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-md font-semibold">
                      {market.backups.length} {market.backups.length === 1 ? "reserve" : "reserver"} klar
                    </span>
                    {market.retired.length > 0 && (
                      <span className="text-gray-400 bg-gray-100 px-2 py-1 rounded-md">
                        {market.retired.length} udfasede
                      </span>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="p-5 space-y-5">
                  {/* Active Sending Domain Box */}
                  <div>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Nuværende aktivt afsenderdomæne
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-emerald-50/70 border border-emerald-200">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-bold text-gray-900">
                            {market.activeDomain.domain}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100/90 px-2 py-0.5 rounded-full">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Aktiv i FeedBlitz
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                          <span>
                            Afsender: <code className="text-gray-800 font-mono">{market.activeDomain.senderEmail}</code>
                          </span>
                          <span>
                            Aktiv siden: <strong className="text-gray-800">{formatDateDanish(market.activeDomain.activeSince)}</strong>
                          </span>
                          <span>
                            Kørt i: <strong className="text-gray-800">{calculateDaysActive(market.activeDomain.activeSince)} dage</strong>
                          </span>
                          <span>
                            Domænealder: <strong className="text-gray-800">{calculateDomainAge(market.activeDomain.createdAt)}</strong>
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1 bg-white px-2.5 py-1 rounded-md border border-emerald-200 text-emerald-800 font-medium shadow-2xs">
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> SPF & DKIM OK
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Backups Table */}
                  <div>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Tilgængelige reserve-domæner ({market.backups.length})
                    </div>
                    {market.backups.length > 0 ? (
                      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-gray-50 text-gray-500 font-semibold border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-2.5">Domæne</th>
                              <th className="px-4 py-2.5">Niche / Type</th>
                              <th className="px-4 py-2.5">Alder</th>
                              <th className="px-4 py-2.5">DNS Status</th>
                              <th className="px-4 py-2.5 text-right">Handling</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {market.backups.map((backup) => (
                              <tr key={backup.domain} className="hover:bg-gray-50/70 transition-colors">
                                <td className="px-4 py-3 font-semibold text-gray-900">
                                  <div>{backup.domain}</div>
                                  {backup.notes && (
                                    <div className="text-[11px] font-normal text-gray-400">{backup.notes}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${
                                    backup.niche === "Offers"
                                      ? "bg-blue-50 text-blue-700"
                                      : backup.niche === "Finans / Lån"
                                      ? "bg-purple-50 text-purple-700"
                                      : "bg-amber-50 text-amber-700"
                                  }`}>
                                    {backup.niche}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  {calculateDomainAge(backup.createdAt)}
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  <span className="inline-flex items-center gap-1.5">
                                    <span className={`w-1.5 h-1.5 rounded-full ${
                                      backup.dnsStatus.includes("Cloudflare") || backup.dnsStatus.includes("Porkbun")
                                        ? "bg-emerald-500"
                                        : "bg-gray-400"
                                    }`} />
                                    {backup.dnsStatus}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => setRotationTarget({ market, newDomain: backup })}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:text-white bg-indigo-50 hover:bg-indigo-600 rounded-md transition-colors cursor-pointer border border-indigo-200 hover:border-indigo-600 shadow-2xs"
                                  >
                                    Skift til dette <ArrowRight className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-xl p-3.5 italic">
                        Ingen friske reserver i puljen i øjeblikket.
                      </div>
                    )}
                  </div>

                  {/* Retired / Used Domains Section */}
                  {market.retired.length > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                      <button
                        onClick={() => toggleRetired(market.geo)}
                        className="flex items-center justify-between w-full text-left py-1 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                      >
                        <span className="flex items-center gap-1.5">
                          <History className="w-3.5 h-3.5 text-gray-400" />
                          Tidligere brugte domæner ({market.retired.length})
                          <span className="text-[11px] font-normal text-gray-400">— Udfaset / genbruges ikke</span>
                        </span>
                        {isRetiredOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </button>

                      {isRetiredOpen && (
                        <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden bg-gray-50/50">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-gray-100/70 text-gray-500 font-semibold border-b border-gray-200">
                              <tr>
                                <th className="px-4 py-2">Domæne</th>
                                <th className="px-4 py-2">Brugsperiode</th>
                                <th className="px-4 py-2">Noter</th>
                                <th className="px-4 py-2 text-right">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200/60">
                              {market.retired.map((r) => (
                                <tr key={r.domain} className="hover:bg-gray-100/40 transition-colors text-gray-600">
                                  <td className="px-4 py-2.5 font-medium text-gray-700 font-mono">
                                    {r.domain}
                                  </td>
                                  <td className="px-4 py-2.5 text-gray-500">
                                    {r.usedPeriod}
                                  </td>
                                  <td className="px-4 py-2.5 text-gray-500">
                                    {r.notes || "—"}
                                  </td>
                                  <td className="px-4 py-2.5 text-right">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-red-50 text-red-700 border border-red-200/80">
                                      <Ban className="w-3 h-3" /> Genbruges ikke
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Confirmation Modal */}
        {rotationTarget && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <RotateCw className="w-4 h-4 text-indigo-600" />
                  Bekræft domæneskift ({rotationTarget.market.countryName})
                </h3>
                <button
                  onClick={() => setRotationTarget(null)}
                  className="text-gray-400 hover:text-gray-600 p-1 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3 text-xs text-gray-600">
                <p>
                  Du er ved at skifte aktivt afsenderdomæne for <strong>{rotationTarget.market.countryName}</strong>:
                </p>

                <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-2">
                  <div className="flex items-center justify-between text-gray-600">
                    <span>Nuværende (udfases til historik):</span>
                    <span className="font-semibold text-gray-800">{rotationTarget.market.activeDomain.domain}</span>
                  </div>
                  <div className="flex items-center justify-between text-indigo-700 font-bold border-t border-gray-200 pt-1.5">
                    <span>Nyt afsenderdomæne:</span>
                    <span>{rotationTarget.newDomain.domain}</span>
                  </div>
                </div>

                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 text-[11px] space-y-1">
                  <div className="font-semibold flex items-center gap-1">
                    <Ban className="w-3.5 h-3.5 text-amber-700" /> Vigtigt:
                  </div>
                  <p>
                    Det gamle domæne <strong>{rotationTarget.market.activeDomain.domain}</strong> flyttes til listen over udfasede domæner og markeres med <em>Genbruges ikke</em>.
                  </p>
                </div>

                <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-blue-900 space-y-1">
                  <div className="font-semibold flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Næste trin i FeedBlitz:
                  </div>
                  <p className="text-[11px] text-blue-800">
                    Husk at opdatere afsendermailen til <code className="bg-white px-1 py-0.5 rounded font-mono">post@{rotationTarget.newDomain.domain}</code> i FeedBlitz-kontoen (<strong>{rotationTarget.market.feedblitzAccount}</strong>).
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t flex justify-end gap-2">
                <button
                  onClick={() => setRotationTarget(null)}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold cursor-pointer"
                >
                  Annuller
                </button>
                <button
                  onClick={handleRotateConfirm}
                  className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold cursor-pointer shadow-xs"
                >
                  Bekræft rotation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
